/**
 * Uploading a FOLDER whose name may already exist on the server — merge it in,
 * the way FileZilla, WinSCP and Finder's "Merge" do.
 *
 * Before this, dropping `assets/` onto a directory that already had an
 * `assets/` was refused outright ("A file or folder called "assets" is already
 * here."), so updating a deployed folder meant uploading it file by file. Now:
 *
 *  - every sub-folder that is missing is created, every one that exists is
 *    reused;
 *  - every file that does not exist yet is uploaded;
 *  - every file that DOES exist is put to the user with the same Replace / Skip
 *    dialog a single-file upload uses, plus "… all" and Cancel — asked BEFORE
 *    anything is created or uploaded (see transferConflicts.js for why);
 *  - a FILE where the drop has a folder, or a FOLDER where it has a file, can
 *    never be a replace and is reported, not asked.
 *
 * `planFolder` (folderTransfer.js) still walks the dropped tree and enforces its
 * limits; this module only decides what that plan means against what is there.
 * Remote paths only — they are `/`-separated on every server.
 */
import { planTransfers, existingNames } from './transferConflicts';

export const parentOf = (path) => {
  const norm = String(path || '/').replace(/\/+$/, '');
  const at = norm.lastIndexOf('/');
  return at <= 0 ? '/' : norm.slice(0, at);
};

export const leafOf = (path) => {
  const norm = String(path || '').replace(/\/+$/, '');
  return norm.slice(norm.lastIndexOf('/') + 1);
};

/** `"/srv/www"` → a labeller turning `/srv/www/assets/css/a.css` into
 *  `assets/css/a.css`: the name the user dropped, not a server path. */
export function labelUnder(root) {
  const base = String(root || '/').replace(/\/+$/, '');
  return (path) => (path.startsWith(`${base}/`) ? path.slice(base.length + 1) : leafOf(path));
}

/**
 * List every planned directory that ALREADY EXISTS on the server — and only
 * those: a folder that is about to be created has nothing in it to clash with,
 * so a merge into a mostly-new tree costs barely more than a plain upload.
 *
 * `dirs` is `planFolder`'s list: root first, every parent before its children.
 * Call it only when the root exists. Hidden files must be included by `listDir`
 * — an `.htaccess` the check cannot see is one it will overwrite unasked.
 *
 * @returns {Promise<Map<string, Map<string, boolean>>>} dir → (name → isDir)
 */
export async function readRemoteTree(dirs, listDir) {
  const listings = new Map();
  const [root, ...rest] = dirs || [];
  if (!root) return listings;
  listings.set(root, existingNames(await listDir(root)));
  for (const dir of rest) {
    const parent = listings.get(parentOf(dir));
    if (parent && parent.get(leafOf(dir)) === true) {
      // eslint-disable-next-line no-await-in-loop
      listings.set(dir, existingNames(await listDir(dir)));
    }
  }
  return listings;
}

/**
 * The merge decision. Pure: the dropped tree (`planFolder`'s `dirs` + `files`)
 * and the listings of whatever already exists → what to do with each piece.
 *
 * `listings` holds an entry for every planned directory that exists; an empty
 * map means the folder is new, and then everything is a plain create + upload.
 *
 * @returns {{
 *   mkdirs: string[],              directories to create, parents first
 *   uploads: object[],             files that clash with nothing
 *   conflicts: object[],           files that would replace an existing file
 *   blocked: Array<{path: string, kind: 'dirOverFile'|'fileOverDir'}>,
 *   unreachable: object[],         files under a blocked folder
 * }}
 */
export function planMerge({ dirs, files, listings }) {
  const state = new Map(); // dir → 'exists' | 'new' | 'blocked'
  const mkdirs = [];
  const uploads = [];
  const conflicts = [];
  const blocked = [];
  const unreachable = [];
  const known = listings || new Map();

  (dirs || []).forEach((dir, index) => {
    if (index === 0) {
      const exists = known.has(dir);
      state.set(dir, exists ? 'exists' : 'new');
      if (!exists) mkdirs.push(dir);
      return;
    }
    const parentState = state.get(parentOf(dir));
    if (parentState === 'new') {
      state.set(dir, 'new');
      mkdirs.push(dir);
      return;
    }
    if (parentState !== 'exists') {
      // Under a blocked folder (or, defensively, under one the plan never
      // named): nothing below it can be placed.
      state.set(dir, 'blocked');
      return;
    }
    const there = known.get(parentOf(dir))?.get(leafOf(dir));
    if (there === undefined) {
      state.set(dir, 'new');
      mkdirs.push(dir);
    } else if (there === true) {
      state.set(dir, 'exists');
    } else {
      // A FILE is where this folder would go. Replacing it would mean deleting
      // a file the user never mentioned, so the folder is reported instead.
      state.set(dir, 'blocked');
      blocked.push({ path: dir, kind: 'dirOverFile' });
    }
  });

  for (const file of files || []) {
    const dir = parentOf(file.to);
    const parentState = state.get(dir);
    if (parentState === 'new') uploads.push(file);
    else if (parentState !== 'exists') unreachable.push(file);
    else {
      const there = known.get(dir)?.get(leafOf(file.to));
      if (there === undefined) uploads.push(file);
      else if (there === false) conflicts.push(file);
      else blocked.push({ path: file.to, kind: 'fileOverDir' });
    }
  }

  return { mkdirs, uploads, conflicts, blocked, unreachable };
}

/**
 * Run a folder upload end to end, over injected I/O so every branch is tested.
 *
 * Order matters, and each step is a promise to the user:
 *  1. read what exists, 2. decide, 3. ASK about every clash — so Cancel leaves
 *  the server exactly as it was, 4. create the missing folders — a failure
 *  here throws before a single file has moved, 5. upload, one file at a time,
 *  where one failure is recorded and the rest carry on.
 *
 * @param {object} args
 * @param {{dirs: string[], files: object[]}} args.plan  from `planFolder`
 * @param {boolean} args.rootExists   the dropped folder's name is a FOLDER here
 * @param {(dir: string) => Promise<object[]>} args.listDir  hidden files included
 * @param {(dir: string) => Promise<void>} args.mkdir
 * @param {(q: object) => Promise<{action: string, all?: boolean}>} args.ask
 * @param {(file: object, replaced: boolean) => Promise<void>} args.upload
 * @param {(path: string) => string} [args.label]  what the user is shown
 * @param {(files: object[], replaced: Set<object>) => void} [args.onQueue]
 *        told every file that WILL be uploaded, before the first one starts
 * @param {(files: object[]) => void} [args.onSkip]  the files the user skipped
 */
export async function runFolderMerge({
  plan,
  rootExists,
  listDir,
  mkdir,
  ask,
  upload,
  label = leafOf,
  onQueue,
  onSkip,
}) {
  const listings = rootExists ? await readRemoteTree(plan.dirs, listDir) : new Map();
  const merge = planMerge({ dirs: plan.dirs, files: plan.files, listings });

  const asked = merge.conflicts.map((file) => ({ name: label(file.to), file }));
  const decided = await planTransfers({
    items: asked,
    existing: new Map(merge.conflicts.map((file) => [label(file.to), false])),
    ask: (question) => ask({ ...question, cancellable: true }),
  });
  const blocked = merge.blocked.map((b) => ({ ...b, label: label(b.path) }));
  if (decided.cancelled) {
    return { cancelled: true, uploaded: 0, created: 0, skipped: [], blocked, failed: [] };
  }

  for (const dir of merge.mkdirs) {
    // eslint-disable-next-line no-await-in-loop
    await mkdir(dir);
  }

  // Plan order, so a replaced file does not jump ahead of the new ones.
  const replaced = new Set(decided.toStart.map((item) => item.file));
  const go = new Set([...merge.uploads, ...replaced]);
  const queue = plan.files.filter((file) => go.has(file));
  const skippedNames = new Set(decided.skipped);
  if (onQueue) onQueue(queue, replaced);
  if (onSkip) onSkip(asked.filter((item) => skippedNames.has(item.name)).map((item) => item.file));
  const failed = [];
  for (const file of queue) {
    try {
      // Sequential on purpose: a browser-dropped file streams through JS, and
      // a few hundred at once would hold every one of them in memory.
      // eslint-disable-next-line no-await-in-loop
      await upload(file, replaced.has(file));
    } catch (err) {
      failed.push({ name: label(file.to), error: err?.message || String(err) });
    }
  }

  return {
    cancelled: false,
    uploaded: queue.length - failed.length,
    created: merge.mkdirs.length,
    skipped: decided.skipped,
    blocked,
    failed,
  };
}
